import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Alert,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import dayjs from "dayjs";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import CustomLabel from "components/CustomLabel";
import { useTheme } from "@mui/material/styles";
import DateRangeCalendar from "screens/Customers/component/DateRangeCalendar";
import { GetSalesTaxReport } from "../logic/SalesTaxReportLogic";
import { GetSalesTaxes } from "screens/Settings/logic/CompanySettingsLogic";
import { GetService } from "screens/Services/logic/GetService";
import ImportExportOptions from "components/ImportExportOptions";

const SalesTaxReport = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [salesTaxData, setSalesTaxData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedTaxJurisdictions, setSelectedTaxJurisdictions] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [taxJurisdictionOptions, setTaxJurisdictionOptions] = useState([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = (option) => {
    setAnchorEl(null);
    if (option) {
      console.log("Export as:", option);
      // TODO: export logic
    }
  };

// Transform API data to match the expected format
const transformSalesTaxData = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) return [];
  
  return apiData.map((item, index) => ({
    id: item.invoiceId || index + 1,
    invoiceId: `INV-${item.invoiceId}` || 'N/A',
    customerName: item.customerName || 'N/A',
    county: item.countyTax || 'N/A',
    serviceType: item.serviceType || 'N/A',
    taxableAmount: `$${(item.taxableAmount || 0).toFixed(2)}`,
    taxRate: `${(item.taxRate || 0).toFixed(2)}%`,
    taxAmount: `$${(item.taxAmount || 0).toFixed(2)}`,
    salesTaxCollected: `$${(item.totalCollected || 0).toFixed(2)}`,
    jobCount: item.quantity || 0
  }));
};

// Fetch sales tax data
const fetchSalesTaxData = async () => {
  setLoading(true);
  setError(null);
  try {
    // Convert arrays to comma-separated strings, or empty string if no selection
    const taxJurisdictionsParam = selectedTaxJurisdictions.length > 0 ? selectedTaxJurisdictions.join(',') : '';
    const serviceTypesParam = selectedServiceTypes.length > 0 ? selectedServiceTypes.join(',') : '';
    console.log('serviceTypesParam:', serviceTypesParam);
    // Now the service directly returns the result array or empty array
    const resultData = await GetSalesTaxReport(startDate, endDate, taxJurisdictionsParam, serviceTypesParam, navigate);
    
    
    // Since the service always returns an array (either data or empty), we can directly transform it
    const transformedData = transformSalesTaxData(resultData);
    console.log(transformedData)
    setSalesTaxData(transformedData);
    
    // If we get an empty array, we might want to show a message
    if (resultData.length === 0) {
      setError('No sales tax data found for the selected filters.');
    }
    
  } catch (err) {
    setError('An error occurred while fetching sales tax data');
    console.error('Sales tax fetch error:', err);
    setSalesTaxData([]); // Ensure empty array on error
  } finally {
    setLoading(false);
  }
};

// Fetch filter options
const fetchFilterOptions = async () => {
  setFiltersLoading(true);
  try {
    // Fetch Tax Jurisdictions from GetSalesTaxes
    const taxResponse = await GetSalesTaxes(0);
    console.log(taxResponse);
    if (taxResponse && !taxResponse.isError && taxResponse.result) {
      const uniqueCounties = [...new Set(taxResponse.result.map(item => item.county))]
        .filter(county => county) // Remove null/undefined
        .sort()
        .map(county => ({ value: county, label: county }));
      setTaxJurisdictionOptions(uniqueCounties);
    }

    // Fetch Service Types from GetService
    const serviceResponse = await GetService(0, 100000, 1, '', navigate);
    if (serviceResponse && !serviceResponse.isError && serviceResponse.result) {
      const uniqueServices = [...new Set(serviceResponse.result.map(item => item.serviceOfferingName))]
        .filter(service => service) // Remove null/undefined
        .sort()
        .map(service => ({ value: service, label: service }));
      setServiceTypeOptions(uniqueServices);
    }
    console.log(serviceResponse);
  } catch (err) {
    console.error('Error fetching filter options:', err);
  } finally {
    setFiltersLoading(false);
  }
};

// Load data and filter options on component mount
useEffect(() => {
  fetchSalesTaxData();
  fetchFilterOptions();
}, []);

  // Handle filter changes
  // Updated handler for Tax Jurisdiction with "all" option
  const handleTaxJurisdictionChange = (event) => {
    const value = event.target.value;
    
    if (value.includes('all')) {
      // If "All" is selected, select all options or deselect all
      if (selectedTaxJurisdictions.length === taxJurisdictionOptions.length) {
        setSelectedTaxJurisdictions([]);
      } else {
        setSelectedTaxJurisdictions(taxJurisdictionOptions.map(option => option.value));
      }
    } else {
      setSelectedTaxJurisdictions(value);
    }
  };

  // Updated handler for Service Type with "all" option
  const handleServiceTypeChange = (event) => {
    const value = event.target.value;
    
    if (value.includes('all')) {
      // If "All" is selected, select all options or deselect all
      if (selectedServiceTypes.length === serviceTypeOptions.length) {
        setSelectedServiceTypes([]);
      } else {
        setSelectedServiceTypes(serviceTypeOptions.map(option => option.value));
      }
    } else {
      setSelectedServiceTypes(value);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Handle run filter
  const handleRunFilter = () => {
    fetchSalesTaxData();
  };

  // DataGrid columns
  const columns = [
    { field: "invoiceId", headerName: "Invoice ID", flex: 1 },
    { field: "customerName", headerName: "Customer Name", flex: 1.5 },
    { field: "county", headerName: "County", flex: 1 },
    { field: "serviceType", headerName: "Service Type", flex: 1 },
    { field: "taxableAmount", headerName: "Taxable Amount", flex: 1 },
    { field: "taxRate", headerName: "Tax Rate (%)", flex: 1 },
    { field: "taxAmount", headerName: "Tax Amount", flex: 1 },
    {
      field: "salesTaxCollected",
      headerName: "Sales Tax Collected",
      flex: 1.2,
    },
    { field: "jobCount", headerName: "Job Count", flex: 1 },
  ];
 const handleNavigation = (to) => {
   
      navigate(to);
    
  };

  const handlePageBack = () => {
    handleNavigation('/reports');    
  }

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Sales Tax Report"
    >
      <Card
        sx={{
          p: 3,
          boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          height: "100%",
          width: "100%",
          overflowX: "auto",
          mx: "auto",
          mt: 4,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              p: 3,
              bgcolor: "white",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h4">Sales Tax Report</Typography>
                <Typography variant="body1" color="text.secondary">
                  Export tax reports that aggregate taxable revenue, applied
                  rates, and tax amounts across different jurisdictions.
                </Typography>
              </Box>
              <ImportExportOptions 
                handleOpenBulkImportDlg={()=>{}}                          
                data={salesTaxData}
                module="SalesTax"
              />  
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose(null)}
              >
                <MenuItem onClick={() => handleClose("csv")}>CSV</MenuItem>
                <MenuItem onClick={() => handleClose("xls")}>XLS</MenuItem>
                <MenuItem onClick={() => handleClose("pdf")}>PDF</MenuItem>
              </Menu>
            </Box>
            <Divider sx={{ mb: 5 }} />

            {/* Filters */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "#F8FAFC" }}
              >
                <Typography variant="h5">Filters</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: "#F8FAFC" }}>
                <Grid container spacing={3}>
                  {/* First Row */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        gap: 5,
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Date Range */}
                      <Box sx={{ width: "200px" }}>
                        <CustomLabel label="Date Range" />
                        <DateRangeCalendar
                          initialStartDate={dayjs(startDate)}
                          initialEndDate={dayjs(endDate)}
                          onDateChange={handleDateRangeChange}
                          sx={{
                            width: "200px !important",
                            maxWidth: "200px !important",
                            '& .MuiOutlinedInput-root': {
                              width: "200px !important",
                              maxWidth: "200px !important",
                            }
                          }}
                        />
                      </Box>

                      {/* Tax Jurisdiction Dropdown */}
                      <Box sx={{ width: "320px" }}>
                        <CustomTextField
                          select
                          fullWidth
                          size="small"
                          label="Tax Jurisdiction"
                          value={selectedTaxJurisdictions}
                          onChange={handleTaxJurisdictionChange}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected || selected.length === 0) {
                                return "Select";
                              }
                              if (selected.length === taxJurisdictionOptions.length) {
                                return "Selected";
                              }
                              if (selected.length === 1) {
                                return selected[0];
                              }
                              return `${selected.length} jurisdictions selected`;
                            },
                            MenuProps: {
                              PaperProps: {
                                sx: {
                                  maxHeight: 220,
                                  width: 320,
                                }
                              }
                            }
                          }}
                          sx={{
                            width: "320px !important",
                            maxWidth: "320px !important",
                            "& .MuiInputBase-root": {
                              width: "100% !important",
                              border: "1px solid #D9D9D9",
                              boxShadow: "none",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#1B2431"
                                  : "#fff !important",
                            },
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              width: "320px !important",
                              maxWidth: "320px !important",
                            }
                          }}
                        >
                          <MenuItem key="all" value="all">
                            <Checkbox checked={selectedTaxJurisdictions.length === taxJurisdictionOptions.length} />
                            <ListItemText 
                              primary="All" 
                              sx={{
                                '& .MuiTypography-root': {
                                  fontSize: '0.875rem',
                                  fontWeight: 'bold',
                                }
                              }}
                            />
                          </MenuItem>
                          {taxJurisdictionOptions.map((jurisdiction) => (
                            <MenuItem key={jurisdiction.value} value={jurisdiction.value}>
                              <Checkbox checked={selectedTaxJurisdictions.includes(jurisdiction.value)} />
                              <ListItemText 
                                primary={jurisdiction.label} 
                                sx={{
                                  '& .MuiTypography-root': {
                                    fontSize: '0.875rem',
                                  }
                                }}
                              />
                            </MenuItem>
                          ))}
                        </CustomTextField>
                      </Box>

                      {/* Service Type Dropdown */}
                      <Box sx={{ width: "340px" }}>
                        <CustomTextField
                          select
                          fullWidth
                          size="small"
                          label="Service Type"
                          value={selectedServiceTypes}
                          onChange={handleServiceTypeChange}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected || selected.length === 0) {
                                return "Select";
                              }
                              if (selected.length === serviceTypeOptions.length) {
                                return "Selected";
                              }
                              if (selected.length === 1) {
                                return selected[0];
                              }
                              return `${selected.length} service types selected`;
                            },
                            MenuProps: {
                              PaperProps: {
                                sx: {
                                  maxHeight: 220,
                                  width: 340,
                                }
                              }
                            }
                          }}
                          sx={{
                            width: "340px !important",
                            maxWidth: "340px !important",
                            "& .MuiInputBase-root": {
                              width: "100% !important",
                              border: "1px solid #D9D9D9",
                              boxShadow: "none",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#1B2431"
                                  : "#fff !important",
                            },
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              width: "340px !important",
                              maxWidth: "340px !important",
                            }
                          }}
                        >
                          <MenuItem key="all" value="all">
                            <Checkbox checked={selectedServiceTypes.length === serviceTypeOptions.length} />
                            <ListItemText 
                              primary="All" 
                              sx={{
                                '& .MuiTypography-root': {
                                  fontSize: '0.875rem',
                                  fontWeight: 'bold',
                                }
                              }}
                            />
                          </MenuItem>
                          {serviceTypeOptions.map((service) => (
                            <MenuItem key={service.value} value={service.value}>
                              <Checkbox checked={selectedServiceTypes.includes(service.value)} />
                              <ListItemText 
                                primary={service.label} 
                                sx={{
                                  '& .MuiTypography-root': {
                                    fontSize: '0.875rem',
                                  }
                                }}
                              />
                            </MenuItem>
                          ))}
                        </CustomTextField>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Run Filter Button */}
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                  >
                    <Button 
                      variant="outlined" 
                      color="main" 
                      size="small"
                      onClick={handleRunFilter}
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Run Filter'}
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            {/* Error Display */}
            {/* {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
            )} */}

            {/* Loading State */}
            {/* {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
                <CircularProgress />
              </Box>
            )} */}

            {/* DataGrid */}
            <Box sx={{ height: 400, width: "100%", mt: 5 }}>
              <DataGridPro
                rows={salesTaxData}
                columns={columns}
                loading={loading}
                disableRowSelectionOnClick
                // pageSizeOptions={[5, 10, 25, 50]}
                // initialState={{
                //   pagination: { paginationModel: { pageSize: 10 } },
                // }}
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    textTransform: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    textTransform: "none",
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        {loading ? 'Loading sales tax data...' : 'No sales tax data found for the selected filters.'}
                      </Typography>
                    </Box>
                  ),
                  footer: () => null
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default SalesTaxReport;